// "My Notes" private scratchpad API (/api/users/me/notes). Strictly self-scoped: the
// backend keys every block on the bearer's own user id, so no endpoint takes a user id.
// Blocks: { id, type: 'note'|'todos'|'file', text?, items?, file?, createdAt, updatedAt }.

import { apiRequest, ApiError } from '@/lib/api/client';
import { getAuthToken } from '@/lib/api/token-store';
import { fetchAuthorizedMediaBlob } from '@/lib/realtime/message-attachments';

function createAuthHeaders(token, headers = {}) {
  const normalizedToken = String(token || getAuthToken() || '').trim();
  if (!normalizedToken) {
    throw new ApiError('authorization token is required', 401);
  }
  return {
    Authorization: `Bearer ${normalizedToken}`,
    ...headers,
  };
}

export const USER_NOTE_BLOCK_TYPES = Object.freeze(['note', 'todos', 'file']);

function normalizeNoteTodoItem(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = String(raw.id || '').trim();
  const label = String(raw.label || '').trim();
  if (!id || !label) return null;
  return {
    id,
    label,
    done: raw.done === true,
    doneAt: raw.doneAt || null,
  };
}

function normalizeNoteFile(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const fileId = String(raw.fileId || '').trim();
  if (!fileId) return null;
  return {
    fileId,
    filename: String(raw.filename || '').trim() || 'file',
    size: Number.isFinite(Number(raw.size)) ? Number(raw.size) : 0,
    mimeType: String(raw.mimeType || '').trim(),
    kind: String(raw.kind || '').trim() || 'file',
  };
}

export function normalizeNoteBlock(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = String(raw.id || '').trim();
  const type = String(raw.type || '').trim();
  if (!id || !USER_NOTE_BLOCK_TYPES.includes(type)) return null;
  const block = {
    id,
    type,
    text: String(raw.text || ''),
    items: [],
    file: null,
    createdAt: raw.createdAt || null,
    updatedAt: raw.updatedAt || null,
  };
  if (type === 'todos') {
    block.items = (Array.isArray(raw.items) ? raw.items : []).map(normalizeNoteTodoItem).filter(Boolean);
    if (block.items.length === 0) return null;
  }
  if (type === 'file') {
    block.file = normalizeNoteFile(raw.file);
    if (!block.file) return null;
  }
  return block;
}

function normalizeNoteBlockList(raw) {
  return (Array.isArray(raw) ? raw : []).map(normalizeNoteBlock).filter(Boolean);
}

/** GET /api/users/me/notes — every live block, newest first. */
export async function fetchUserNotes({ token, signal } = {}) {
  const payload = await apiRequest('/api/users/me/notes', {
    method: 'GET',
    headers: createAuthHeaders(token),
    signal,
  });
  return normalizeNoteBlockList(payload?.blocks);
}

/** POST /api/users/me/notes — create a note ({type:'note', text}) or todos ({type:'todos', items}) block. */
export async function createUserNoteBlock({ token, type, text, items } = {}) {
  const body = { type };
  if (type === 'note') {
    body.text = String(text || '');
  } else if (type === 'todos') {
    body.items = (Array.isArray(items) ? items : []).map((item) => ({
      id: String(item?.id || '').trim(),
      label: String(item?.label || '').trim(),
      done: item?.done === true,
      doneAt: item?.doneAt || undefined,
    }));
  } else {
    throw new ApiError('unsupported block type', 400);
  }
  const payload = await apiRequest('/api/users/me/notes', {
    method: 'POST',
    headers: createAuthHeaders(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  return normalizeNoteBlock(payload);
}

/** PUT /api/users/me/notes/{blockID} — update a note's text or a todos block's full items list. */
export async function updateUserNoteBlock({ token, blockId, text, items } = {}) {
  const normalizedId = String(blockId || '').trim();
  if (!normalizedId) {
    throw new ApiError('blockId is required', 400);
  }
  const body = {};
  if (text !== undefined) body.text = String(text || '');
  if (items !== undefined) {
    body.items = (Array.isArray(items) ? items : []).map((item) => ({
      id: String(item?.id || '').trim(),
      label: String(item?.label || '').trim(),
      done: item?.done === true,
      doneAt: item?.doneAt || undefined,
    }));
  }
  const payload = await apiRequest(`/api/users/me/notes/${encodeURIComponent(normalizedId)}`, {
    method: 'PUT',
    headers: createAuthHeaders(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  return normalizeNoteBlock(payload);
}

/** DELETE /api/users/me/notes/{blockID} — soft-delete one block (204). */
export async function deleteUserNoteBlock({ token, blockId } = {}) {
  const normalizedId = String(blockId || '').trim();
  if (!normalizedId) {
    throw new ApiError('blockId is required', 400);
  }
  await apiRequest(`/api/users/me/notes/${encodeURIComponent(normalizedId)}`, {
    method: 'DELETE',
    headers: createAuthHeaders(token),
  });
  return true;
}

/**
 * POST /api/users/me/notes/files?filename=… — raw-body upload (the request body IS the
 * file bytes, Content-Type is its MIME). Uploads to GridFS and creates the file block in
 * one call; resolves to the created block.
 */
export async function uploadUserNoteFile({ token, file } = {}) {
  if (!(file instanceof Blob)) {
    throw new ApiError('file is required', 400);
  }
  const filename = String(file.name || '').trim() || 'upload';
  const payload = await apiRequest(
    `/api/users/me/notes/files?filename=${encodeURIComponent(filename)}`,
    {
      method: 'POST',
      headers: createAuthHeaders(token, {
        'Content-Type': String(file.type || '').trim() || 'application/octet-stream',
      }),
      body: file,
    },
  );
  return normalizeNoteBlock(payload);
}

/** Owner-only file download → Blob (bearer-authorized fetch; not apiRequest — the body is bytes, not JSON). */
export async function getUserNoteFileBlob(fileId) {
  const normalizedId = String(fileId || '').trim();
  if (!normalizedId) {
    throw new ApiError('fileId is required', 400);
  }
  return fetchAuthorizedMediaBlob(`/api/users/me/notes/files/${encodeURIComponent(normalizedId)}`);
}
